import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const imageFullSchema = z.object({
  image: z.string().min(1).max(1000),
  alt: z.string().max(200).optional(),
  tag: z.string().max(80).optional(),
  title: z.string().min(1).max(300),
  highlight: z.string().max(160).optional(),
  description: z.string().max(1000).optional(),
  ctaLabel: z.string().max(80).optional(),
  ctaHref: z.string().max(500).optional(),
  cardHead: z.string().max(120).optional(),
  stats: z
    .array(
      z.object({
        value: z.string().min(1).max(40),
        label: z.string().min(1).max(120),
      }),
    )
    .max(6)
    .optional(),
  progress: z
    .array(
      z.object({
        label: z.string().min(1).max(120),
        meta: z.string().min(1).max(60),
        done: z.boolean().default(true),
      }),
    )
    .max(10)
    .optional(),
});

export type ImageFullData = z.infer<typeof imageFullSchema>;

export function ImageFullRenderer({
  image,
  alt,
  tag,
  title,
  highlight,
  description,
  ctaLabel,
  ctaHref,
  cardHead,
  stats,
  progress,
}: ImageFullData) {
  return (
    <section className="v5-image-full">
      <img className="v5-image-full-bg" src={image} alt={alt ?? ''} />
      <div className="v5-image-full-overlay"></div>
      <div className="container v5-image-full-grid">
        <div className="v5-feat-text">
          {tag ? <div className="v5-feat-tag">{tag}</div> : null}
          <h2 className="v5-feat-h2">
            {title}
            {highlight ? (
              <>
                <br />
                <span className="v5-feat-hl">{highlight}</span>
              </>
            ) : null}
          </h2>
          {description ? <p className="v5-feat-p">{description}</p> : null}
          {ctaLabel && ctaHref ? (
            <a href={ctaHref} className="v3-btn v3-btn-primary v3-btn-lg">
              {ctaLabel} →
            </a>
          ) : null}
        </div>
        {(stats && stats.length > 0) || (progress && progress.length > 0) || cardHead ? (
          <div className="v5-feat-card">
            {cardHead ? <div className="v5-feat-card-head">{cardHead}</div> : null}
            {stats && stats.length > 0 ? (
              <div className="v5-feat-stats">
                {stats.map((s, i) => (
                  <div key={i}>
                    <strong>{s.value}</strong>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {progress && progress.length > 0 ? (
              <div className="v5-feat-progress">
                {progress.map((p, i) => (
                  <div key={i} className="v5-feat-progress-row">
                    <span>{p.label}</span>
                    <span className={p.done ? 'ok' : ''}>
                      {p.done ? '✓ ' : ''}
                      {p.meta}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export const imageFullBlock: BlockDefinition<ImageFullData> = {
  meta: {
    type: 'image-full',
    label: 'Imagen full + tarjeta caso (v5)',
    description: 'Sección a sangre con imagen de fondo, texto a la izquierda y card con stats + progreso a la derecha.',
    category: 'media',
    icon: 'image',
  },
  schema: imageFullSchema,
  defaults: {
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=2400&h=1200&fit=crop&q=85&auto=format',
    alt: 'Sala de reuniones',
    tag: 'Caso destacado · 2025',
    title: 'De empresa familiar a grupo internacional.',
    highlight: '9 meses, NDA total.',
    description: 'Acompañamos al fundador de una industrial familiar (35 años, €8M facturación) en su salida por jubilación.',
    ctaLabel: 'Ver caso completo',
    ctaHref: '/portfolio',
    cardHead: 'Operación cerrada',
    stats: [
      { value: '€12M', label: 'operación cerrada' },
      { value: '9 meses', label: 'de inicio a cierre' },
      { value: '4', label: 'candidatos finalistas' },
      { value: '100%', label: 'NDA todo el proceso' },
    ],
    progress: [
      { label: 'Diagnóstico inicial', meta: 'Mes 1', done: true },
      { label: 'Cuaderno de venta', meta: 'Mes 2', done: true },
      { label: 'Búsqueda compradores', meta: 'Mes 3-5', done: true },
      { label: 'Negociación & DD', meta: 'Mes 6-8', done: true },
      { label: 'Cierre & firma', meta: 'Mes 9', done: true },
    ],
  },
  fields: [
    { name: 'image', label: 'Imagen fondo', type: 'image', required: true },
    { name: 'alt', label: 'Alt', type: 'text', max: 200 },
    { name: 'tag', label: 'Tag', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 300 },
    { name: 'highlight', label: 'Highlight', type: 'text', max: 160 },
    { name: 'description', label: 'Descripción', type: 'textarea', max: 1000 },
    { name: 'ctaLabel', label: 'Texto CTA', type: 'text', max: 80 },
    { name: 'ctaHref', label: 'URL CTA', type: 'url' },
    { name: 'cardHead', label: 'Cabecera card', type: 'text', max: 120 },
    {
      name: 'stats',
      label: 'Stats card',
      type: 'array',
      max: 6,
      itemFields: [
        { name: 'value', label: 'Valor', type: 'text', required: true, max: 40 },
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 120 },
      ],
    },
    {
      name: 'progress',
      label: 'Progress (timeline)',
      type: 'array',
      max: 10,
      itemFields: [
        { name: 'label', label: 'Label', type: 'text', required: true, max: 120 },
        { name: 'meta', label: 'Meta (Mes X)', type: 'text', required: true, max: 60 },
        { name: 'done', label: 'Completado', type: 'boolean' },
      ],
    },
  ],
  Renderer: ImageFullRenderer,
};
